<template>
  <nav
    class="th-navigation pt-6"
    :class="{ collapsed }"
    data-testid="navigation"
  >
    <ul>
      <!-- Menu -->
      <li
        v-for="(item, index) in navigationWithPermissions"
        :key="index"
        class="th-menu"
        :class="{ 'th-menu-with-submenu': item.children.length > 0 }"
        @mouseenter="mouseEnter"
      >
        <!-- Menu item -->
        <div class="th-menu-link">
          <a
            v-if="item.enabled && item.external"
            :href="item.route"
            :target="item.target || '_blank'"
            :rel="
              (item.target || '_blank') === '_blank'
                ? 'noopener noreferrer'
                : ''
            "
            :data-testid="item.route"
            @click="handleNavigationLinkClick(item)"
          >
            <component :is="item.icon" v-if="item.icon" class="svg-icon" />
            <span :style="style" class="launch-icon-container"
              >{{ item.key ? $t(item.key) : item.name }}
              <launch-icon class="launch-icon"
            /></span>
          </a>

          <router-link
            v-else-if="item.enabled"
            :ref="`link${item.route}`"
            :to="item.route"
            :data-testid="item.route"
            :class="{
              'router-link-active':
                currentPath === item.route ||
                currentPath?.startsWith(item.route + '/'),
              selectable: collapsed || item.children.length === 0
            }"
            @click="handleNavigationLinkClick(item)"
          >
            <component :is="item.icon" v-if="item.icon" class="svg-icon" />

            <span :style="style">{{
              item.key ? $t(item.key) : item.name
            }}</span>
          </router-link>
        </div>

        <!-- Submenu -->
        <div
          v-if="item.children.length > 0"
          class="th-submenu"
          :style="style"
          :class="{ open: submenuOpen === item.name }"
        >
          <!-- Submenu header (only visible in collapsed state) -->
          <div class="th-submenu-header">
            {{ item.key ? $t(item.key) : item.name }}
          </div>
          <!-- Submenu items -->
          <ul>
            <li
              v-for="(subitem, subindex) in item.children"
              :key="`${index}-${subindex}`"
              class="th-submenu-link"
            >
              <router-link
                v-if="subitem.enabled"
                :to="`${item.route}${subitem.route}`"
                :data-testid="`${item.route}${subitem.route}`"
                active-class=""
                exact-active-class=""
                :class="{
                  'router-link-active': isSubmenuLinkActive(
                    item.route,
                    subitem,
                    item.children
                  )
                }"
                class="selectable"
                :tabIndex="!collapsed && submenuOpen === item.name ? 0 : -1"
              >
                {{ subitem.key ? $t(subitem.key) : subitem.name }}

                <div
                  v-if="subitem.deprecated"
                  class="ml-2 inline-flex items-center px-2 h-6 border border-th-border-dark rounded-full overflow-hidden"
                  :title="$t('nav.main.legacy.label')"
                >
                  <span class="truncate text-xs text-th-text-regular">
                    {{ $t('nav.main.legacy.label') }}
                  </span>
                </div>
              </router-link>
            </li>
          </ul>
        </div>
      </li>
    </ul>
  </nav>
</template>

<script>
import { parseNavByPermissions } from '@shared/utils/permissions'
import { isSubmenuLinkActive as resolveSubmenuLinkActive } from '@shared/utils/navigation/submenu-link-active'
import { filterNavByRolloutFlags } from '@shared/utils/navigation/filter-nav-by-rollout-flags'
import { useAppConfigStore } from '@shared/store/app-config'
import { useAuthStore } from '@shared/store/auth'
import { storeToRefs } from 'pinia'
import { isUnifiedCommerce, isKeycloackEnabled } from '@shared/constants'
import { usePosthogFeatureFlags } from '@shared/plugins/posthog'
import { LaunchIcon } from '@shared/assets/icons'

export default {
  components: {
    LaunchIcon
  },
  props: {
    collapsed: {
      type: Boolean,
      default: false
    }
  },

  setup() {
    const appConfigStore = useAppConfigStore()
    const authStore = useAuthStore()
    const { navigation } = storeToRefs(appConfigStore)
    const { scopes: userScopes, role: userRole } = storeToRefs(authStore)
    const rolloutFlags = usePosthogFeatureFlags()

    return { navigation, userScopes, userRole, rolloutFlags }
  },

  data() {
    return {
      style: { top: '' },
      submenuOpen: null,
      currentPath: null
    }
  },

  computed: {
    navigationWithPermissions() {
      if (
        isUnifiedCommerce() &&
        isKeycloackEnabled() &&
        (!this.userScopes || this.userScopes.length === 0)
      ) {
        return []
      }
      return filterNavByRolloutFlags(
        parseNavByPermissions(this.navigation, this.userRole, this.userScopes),
        this.rolloutFlags
      )
    }
  },

  watch: {
    $route: function () {
      this.routeChanged(this.$route.path)
    }
  },

  mounted() {
    this.routeChanged(this.$route.path)
  },

  methods: {
    handleNavigationLinkClick(item) {
      if (!item.external) {
        this.routeChanged(item.route)
      }
    },

    isSubmenuLinkActive(parentRoute, subitem, siblings = []) {
      return resolveSubmenuLinkActive(
        this.currentPath,
        parentRoute,
        subitem,
        siblings
      )
    },
    mouseEnter(e) {
      if (!this.collapsed) return

      const submenu = e.target.getElementsByClassName('th-submenu')[0]
      if (submenu) {
        // Submenu dropdown clipping fix
        // TOOD: This solution needs attention. Sometimes menu name tooltips will end up in the wrong position. Also the menus sometimes jump around a bit.
        setTimeout(() => {
          const submenuTop = e.target.getBoundingClientRect().top
          const submenuHeight = submenu.getBoundingClientRect().height
          if (submenuHeight + submenuTop > window.innerHeight) {
            const diff = submenuHeight + submenuTop - window.innerHeight
            this.style.top = submenuTop - diff + 'px'
          } else {
            this.style.top = submenuTop + 'px'
          }
        }, 100)
      } else {
        this.style.top = e.target.getBoundingClientRect().top + 'px'
      }
    },

    routeChanged(newPath) {
      this.currentPath = newPath
      const path = newPath.substring(1).split('/')
      this.submenuOpen = path[0]
      this.scrollToLink(path[0])
    },

    scrollToLink(path) {
      const element = this.$refs['link/' + path]
      if (element && element[0]?.$el.scrollIntoView) {
        element[0].$el.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest'
        })
      }
    }
  }
}
</script>

<style scoped>
.th-navigation {
  overflow-x: hidden;
  height: 100%;

  /* Navigation colors */
  --bg-color: var(--el-bg-color-page);
  --bg-color--hover: var(--el-fill-color-dark);
  --bg-color--pressed: var(--el-fill-color-darker);
  --item-color: var(--el-text-color-primary);
  --sub-item-color: var(--el-text-color-regular);
  --item-icon-color: var(--el-text-color-regular);
  --color--active: var(--el-color-accent);
  --bg-color--active: var(--el-color-accent-light-9);

  --item-height: 40px;
  --item-font-weight: 400;
  --item-font-weight--active: 500;
}

.th-submenu-header {
  display: none;
}

/* .router-link-exact-active, */
.router-link-active.selectable,
.router-link-active.selectable:hover,
.router-link-active.selectable:active {
  background: var(--bg-color--active);
  color: var(--color--active);
  border-color: var(--color--active);
  font-weight: var(--item-font-weight--active);
}

.th-submenu-link a,
.th-menu-link a {
  color: var(--item-color);
  font-size: 0.875em;
  font-weight: var(--item-font-weight);
  height: var(--item-height);
  padding: 0 24px;

  display: flex;
  align-items: center;
  cursor: pointer;

  border-right: 2px solid transparent;
  letter-spacing: var(--letter-spacing-titles);
}

.th-submenu-link a {
  color: var(--sub-item-color);
  padding-left: 52px; /* menu padding + icon width + icon margin */
}

.th-submenu-link a:focus-visible,
.th-menu-link a:focus-visible {
  outline: 2px solid var(--el-color-primary-light-5);
  outline-offset: -2px;
}

.svg-icon {
  height: 20px;
  width: 20px;
  color: var(--item-icon-color);
  margin-right: 8px;
}

.router-link-active.selectable .svg-icon {
  color: var(--color--active);
}

.th-menu-link a:hover,
.th-submenu-link a:hover {
  background: var(--bg-color--hover);
}

.th-menu-link a:active,
.th-submenu-link a:active {
  background: var(--bg-color--pressed);
}

.th-submenu {
  overflow: hidden;
  transition: all 0.5s ease;
  max-height: 0;
}

.th-submenu.open {
  max-height: 1000px;
}

/* Scroll bar customization for firefox */
.th-navigation {
  --scrollbar-foreground: var(--el-border-color);
  --scrollbar-background: transparent;
  scrollbar-color: var(--scrollbar-foreground) var(--scrollbar-background);
  scrollbar-width: thin;
}
/* Scroll bar customization for chrome and safari */
.th-navigation::-webkit-scrollbar {
  width: 9px;
}
.th-navigation::-webkit-scrollbar-thumb {
  border-radius: 4px;
  background: var(--scrollbar-foreground);
}
.th-navigation::-webkit-scrollbar-track {
  background: var(--scrollbar-background);
}

/* Collapsed state */
.th-navigation.collapsed .th-submenu-header {
  display: block;
  color: var(--el-text-color-primary);
  padding: 0 16px 4px 16px;
  font-size: 0.8125rem;
  line-height: 22px;
  padding-left: 16px;
}

.th-navigation.collapsed .th-menu-link a {
  padding: 0;
  display: flex;
  justify-content: center;
  align-items: center;
}

.th-navigation.collapsed
  .th-menu:not(.th-menu-with-submenu):hover
  .th-menu-link
  a
  span {
  display: flex;
}

/* Tooltip for items with no submenu in collapsed state */
.th-navigation.collapsed .th-menu-link a span {
  z-index: 1000;
  position: fixed;
  left: 65px;
  background: var(--el-color-black);
  color: var(--el-color-white);
  font-weight: 500;
  display: none;
  align-items: center;
  padding: 2px 8px;
  border-radius: 20px;
  font-size: 0.8125rem;
  line-height: 22px;
  margin-left: 0.5rem;
  margin-top: calc(var(--item-height) / 2 - 11px);
}

.th-navigation.collapsed .th-menu-link a svg {
  margin-right: 0;
}

.th-navigation.collapsed .th-submenu.open {
  max-height: 0;
}

.th-navigation.collapsed .th-menu:hover .th-submenu {
  max-height: 1000px;
  background: var(--bg-color);
  padding: 8px 0;
  border: 1px solid var(--el-border-color);
  border-left: none;
  min-width: 200px;
}

.th-navigation.collapsed .th-submenu-link a {
  height: var(--item-height);
  padding-left: 16px;
}

.th-navigation.collapsed .th-submenu {
  transition: all 0.1s;
  position: fixed;
  top: 50%;
  left: 65px;
  z-index: 10000;
  transform-origin: top left;
}

.launch-icon-container {
  display: flex;
  flex-direction: row;
  align-items: center;
}

.launch-icon {
  height: 16px;
  width: 16px;
  margin-left: 8px;
  display: inline-block;
  fill: currentColor;
  opacity: 0.7;
}

.th-menu-with-separator {
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  margin-top: 1rem;
  padding-top: 1rem;
}
</style>
